import { useRouter } from 'expo-router';
import { CalendarClock, Check, Lock, RotateCcw, SkipForward } from 'lucide-react-native';
import { ComponentType, useMemo, useRef, useState } from 'react';
import { PanResponder, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';

import { useConfirm } from '@/components/ConfirmDialog';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius } from '@/constants/theme';
import { getSubtypeMeta, getSystemMeta } from '@/data/systems';
import { webTransition } from '@/lib/hover-style';
import type { ActiveTask } from '@/lib/tasks';
import { resolveIcon } from '@/lib/rule-icon';

interface Props {
  task: ActiveTask;
  /** Round-checkbox tap → animates the row out + fires onComplete with
   *  the screen-space center of the checkbox so the parent can place
   *  confetti right where the user tapped. */
  onComplete: (point: { x: number; y: number }) => void;
  /** Skip — row slides out, parent closes the task with kind='skip'. */
  onSkip?: () => void;
  /** Reschedule — opens the parent's ReschedulePicker for this task. */
  onReschedule?: () => void;
  /** Redo (Completed list only) — uncompletes the task. Replaces the
   *  check/skip/reschedule cluster with a single Redo pill. */
  onRedo?: () => void;
  /** Long-press handler that surfaces the "why was this scheduled?"
   *  popover. Optional — Schedule page wires it, Today doesn't. */
  onWhy?: (task: ActiveTask) => void;
  /** Context menu (Wave 48): long-press (native) or right-click (web)
   *  opens the parent's TaskActionsSheet for this task. When both
   *  onMenu and onWhy are wired, long-press prefers the menu (it
   *  includes every action; "why" stays reachable from task detail). */
  onMenu?: (task: ActiveTask) => void;
}

/**
 * Compact, Todoist-inspired task row. Differences from the original
 * TaskRow:
 *   - Round checkbox sits FIRST (left), not last (right). This mirrors
 *     Todoist / Things / Reminders and lets users mark done with the
 *     same hand-position they used to read the title.
 *   - Title is the dominant element. The day-of-week is downgraded to
 *     a small honey-tinted pill in the meta line, alongside time +
 *     system label.
 *   - Skip + Reschedule live on the right as small ghost buttons.
 *     On desktop they show always; on mobile they collapse to two
 *     icon-only buttons (still always visible — no hover required).
 *   - Denser vertical rhythm (8px padding vs 13px) so 2-3 more rows
 *     fit in the same viewport.
 *   - Desktop gets extra meta (difficulty + system) revealed at ≥720px;
 *     mobile keeps to time + day + skip/reschedule.
 */
export function CompactTaskRow({
  task,
  onComplete,
  onSkip,
  onReschedule,
  onRedo,
  onWhy,
  onMenu,
}: Props) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const wide = width >= 720; // matches the side-by-side Inside/Outside breakpoint

  const system = getSystemMeta(task.system_kind);
  const subtype = getSubtypeMeta(task.system_kind, task.system_subtype);
  const Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }> =
    resolveIcon(task.icon ?? system?.icon ?? null);
  const diff = task.difficulty ?? 'easy';
  const diffLabel = diff === 'easy' ? 'Easy' : diff === 'moderate' ? 'Moderate' : 'Hard';
  const systemLabel = task.system_nickname
    ? task.system_nickname
    : subtype
    ? `${system?.label ?? task.system_kind} · ${subtype.label}`
    : system?.label ?? 'Home';

  // Day pill computation. Format: "Sun" or "Sun 31" on wider screens.
  const dueDate = task.due_at != null ? new Date(task.due_at) : null;
  const dayShort = dueDate
    ? dueDate.toLocaleDateString(undefined, { weekday: 'short' })
    : null;
  const dayNum = dueDate ? dueDate.getDate() : null;

  // Overdue / next-year tinting.
  const overdueDays = (() => {
    if (task.due_at == null) return 0;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (task.due_at >= startOfToday) return 0;
    return Math.floor((startOfToday - task.due_at) / (24 * 60 * 60 * 1000));
  })();
  const isOverdue = overdueDays > 0;
  const overdueLabel = overdueDays === 1 ? '1 day overdue' : `${overdueDays} days overdue`;
  const dueYear = task.due_at != null ? new Date(task.due_at).getFullYear() : null;
  const isNextYear =
    !isOverdue && dueYear != null && dueYear > new Date().getFullYear();

  const checkRef = useRef<View>(null);
  const slide = useSharedValue(0);
  const opacity = useSharedValue(1);
  const rowStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: slide.value }],
    opacity: opacity.value,
  }));
  // Background-reveal layers that show beneath the row when the user
  // swipes — green "Done" on the right, clay "Skip" on the left. Opacity
  // ramps with swipe distance so the user gets visual feedback before
  // the threshold trips.
  const completeBgStyle = useAnimatedStyle(() => ({
    opacity: Math.max(0, Math.min(1, slide.value / 80)),
  }));
  const skipBgStyle = useAnimatedStyle(() => ({
    opacity: Math.max(0, Math.min(1, -slide.value / 80)),
  }));
  const [rowHover, setRowHover] = useState(false);

  // PanResponder for left/right swipe gestures. Only attached when this
  // row has a complete handler (i.e. not in completed/Redo mode).
  // Threshold: 80px of horizontal travel before a release triggers an
  // action. Below the threshold, releasing snaps the row back to home.
  // Vertical drags are NOT captured so users can still scroll the list
  // freely; only mostly-horizontal pans take over.
  const SWIPE_THRESHOLD = 80;
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_e, gesture) => {
          if (onRedo) return false; // completed view doesn't swipe
          // Take over only on mostly-horizontal gestures and only once
          // they've travelled enough to be unambiguous (10px). Anything
          // less and we let the ScrollView keep the pan.
          return (
            Math.abs(gesture.dx) > 10 &&
            Math.abs(gesture.dx) > Math.abs(gesture.dy) * 1.5
          );
        },
        onPanResponderMove: (_e, gesture) => {
          slide.value = gesture.dx;
        },
        onPanResponderRelease: async (_e, gesture) => {
          const dx = gesture.dx;
          if (dx > SWIPE_THRESHOLD) {
            // Right swipe → complete.
            if (!(await guardNextYear('complete'))) {
              slide.value = withTiming(0, { duration: 200 });
              return;
            }
            checkRef.current?.measureInWindow((px, py, w, h) => {
              onComplete({ x: px + w / 2, y: py + h / 2 });
            });
            slide.value = withTiming(400, { duration: 240 });
            opacity.value = withTiming(0, { duration: 240 });
          } else if (dx < -SWIPE_THRESHOLD && onSkip) {
            // Left swipe → skip.
            if (!(await guardNextYear('skip'))) {
              slide.value = withTiming(0, { duration: 200 });
              return;
            }
            slide.value = withTiming(-400, { duration: 240 });
            opacity.value = withTiming(0, { duration: 240 });
            setTimeout(() => onSkip(), 80);
          } else {
            // Threshold not met — snap back.
            slide.value = withTiming(0, { duration: 200 });
          }
        },
        onPanResponderTerminate: () => {
          slide.value = withTiming(0, { duration: 200 });
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onComplete, onSkip, onRedo, isNextYear, dueYear]
  );

  const confirm = useConfirm();

  /**
   * Next-year guard. Tasks scheduled for a future calendar year are
   * generated speculatively by the planner — letting users tap them
   * without warning means they can mark something done in May 2026
   * that the system thinks should happen in March 2027. Block the
   * tap, explain the situation, and let them opt in if they're sure.
   */
  const guardNextYear = async (verb: 'open' | 'complete' | 'skip' | 'reschedule'): Promise<boolean> => {
    if (!isNextYear || !dueYear) return true;
    const verbCopy: Record<typeof verb, string> = {
      open: 'Open it anyway',
      complete: 'Mark it done now',
      skip: 'Skip it anyway',
      reschedule: 'Reschedule it',
    };
    return confirm({
      title: `This task is for ${dueYear}`,
      message:
        `Homestead generates a year ahead so you can see what's coming. ` +
        `It's not due yet — if you tap by accident, you might log it as ` +
        `done in the wrong year. Continue if you really mean to.`,
      confirmLabel: verbCopy[verb],
    });
  };

  const handleCheck = async () => {
    if (!(await guardNextYear('complete'))) return;
    checkRef.current?.measureInWindow((px, py, w, h) => {
      onComplete({ x: px + w / 2, y: py + h / 2 });
    });
    slide.value = withTiming(50, { duration: 280 });
    opacity.value = withTiming(0, { duration: 280 });
  };

  const handleSkip = async () => {
    if (!onSkip) return;
    if (!(await guardNextYear('skip'))) return;
    slide.value = withTiming(-50, { duration: 280 });
    opacity.value = withTiming(0, { duration: 280 });
    setTimeout(onSkip, 60);
  };

  const handleReschedule = async () => {
    if (!onReschedule) return;
    if (!(await guardNextYear('reschedule'))) return;
    onReschedule();
  };

  const handleRedo = () => {
    if (!onRedo) return;
    slide.value = withTiming(-50, { duration: 280 });
    opacity.value = withTiming(0, { duration: 280 });
    setTimeout(onRedo, 60);
  };

  const openDetail = async () => {
    if (!(await guardNextYear('open'))) return;
    router.push(`/task/${task.id}` as never);
  };

  return (
    // Outer wrap is the swipe stage: holds the reveal backgrounds AND
    // the foreground row. PanResponder attached only when this row
    // supports complete/skip (i.e. not in Redo / completed mode).
    <View
      style={styles.swipeWrap}
      {...(onRedo ? {} : panResponder.panHandlers)}>
      {/* Right-reveal "Done" hint — appears when the user swipes right.
          Mirrors the green completion affordance. Inset matches the
          row's vertical metrics so the hint sits flush behind. */}
      <Animated.View style={[styles.swipeBg, styles.swipeBgComplete, completeBgStyle]}>
        <Check size={16} color={Palette.cream} strokeWidth={2.4} />
        <Text style={styles.swipeBgText}>Done</Text>
      </Animated.View>
      {/* Left-reveal "Skip" hint. */}
      <Animated.View style={[styles.swipeBg, styles.swipeBgSkip, skipBgStyle]}>
        <Text style={styles.swipeBgText}>Skip</Text>
        <SkipForward size={16} color={Palette.cream} strokeWidth={2.4} />
      </Animated.View>
    <Animated.View
      style={[
        styles.row,
        isOverdue && styles.rowOverdue,
        isNextYear && styles.rowNextYear,
        rowHover && !isOverdue && styles.rowHover,
        webTransition,
        rowStyle,
      ]}>
      {/* CHECKBOX — first thing the eye lands on. Tap = complete. */}
      {onRedo ? (
        // Completed mode: single Redo pill in place of the checkbox.
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Redo ${task.title}`}
          onPress={handleRedo}
          style={({ pressed, hovered }) => [
            styles.redoPill,
            webTransition,
            hovered && { backgroundColor: '#EAF0EA' },
            pressed && { transform: [{ scale: 0.94 }] },
          ]}>
          <RotateCcw size={12} color={Palette.forest} strokeWidth={2.2} />
          <Text style={styles.redoText}>Redo</Text>
        </Pressable>
      ) : (
        <Pressable
          ref={checkRef}
          accessibilityRole="button"
          accessibilityLabel={`Mark ${task.title} done`}
          onPress={handleCheck}
          hitSlop={6}
          style={({ pressed, hovered }) => [
            styles.checkbox,
            isOverdue && styles.checkboxOverdue,
            webTransition,
            hovered && styles.checkboxHover,
            pressed && { transform: [{ scale: 0.88 }] },
          ]}>
          {/* Checkmark appears only when hovering THE CIRCLE itself —
              not when hovering the row body. This keeps the row read
              as "tap to open" while the circle reads as "tap to
              complete." Pressable's render-function form gives us the
              checkbox-scoped hovered state without needing a separate
              useState + onHoverIn handlers. */}
          {({ hovered }) => (
            <View style={styles.checkboxInner}>
              {hovered ? (
                <Check size={13} color={Palette.forest} strokeWidth={2.6} />
              ) : null}
            </View>
          )}
        </Pressable>
      )}

      {/* BODY — title (prominent) + meta line. Tap = open detail. */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Open ${task.title}`}
        onPress={openDetail}
        onLongPress={
          onMenu ? () => onMenu(task) : onWhy ? () => onWhy(task) : undefined
        }
        delayLongPress={350}
        onHoverIn={() => setRowHover(true)}
        onHoverOut={() => setRowHover(false)}
        // Web: right-click opens the same context menu instead of the
        // browser's. RN-web forwards onContextMenu to the DOM node.
        {...(onMenu
          ? ({
              onContextMenu: (e: { preventDefault?: () => void }) => {
                e?.preventDefault?.();
                onMenu(task);
              },
            } as object)
          : {})}
        style={styles.body}>
        <Text
          style={[
            styles.title,
            isOverdue && styles.titleOverdue,
          ]}
          numberOfLines={1}>
          {task.title}
        </Text>
        <View style={styles.metaLine}>
          {/* Day pill — small honey-tinted, not the visual anchor. */}
          {dayShort ? (
            <View style={[styles.dayPill, isOverdue && styles.dayPillOverdue]}>
              <CalendarClock
                size={10}
                color={isOverdue ? Palette.clay : Palette.honeyDeep}
                strokeWidth={2.2}
              />
              <Text style={[styles.dayPillText, isOverdue && styles.dayPillTextOverdue]}>
                {wide ? `${dayShort} ${dayNum}` : dayShort}
              </Text>
            </View>
          ) : null}
          {isOverdue ? (
            <Text style={styles.metaOverdue}>{overdueLabel}</Text>
          ) : isNextYear && dueYear ? (
            // Lock cue + "Next year · YYYY" so the user understands this
            // row is gated by the acknowledgement prompt before tapping
            // does anything. Compact enough to fit alongside the day pill.
            <View style={styles.nextYearMeta}>
              <Lock size={9} color={Palette.inkFaint} strokeWidth={2.4} />
              <Text style={styles.metaMuted}>Next year · {dueYear}</Text>
            </View>
          ) : (
            <Text style={styles.meta}>{task.time_minutes ?? 10} min</Text>
          )}
          {/* Desktop-only extras: difficulty + system. Hidden on mobile
              to keep the meta line one-glance scannable. */}
          {wide && !isOverdue && !isNextYear ? (
            <>
              <View style={styles.dot} />
              <Text style={[styles.meta, diff === 'easy' ? styles.easy : styles.moderate]}>
                {diffLabel}
              </Text>
            </>
          ) : null}
          {wide ? (
            <>
              <View style={styles.dot} />
              <View style={styles.systemPill}>
                <Icon size={11} color={Palette.forest} strokeWidth={1.9} />
                <Text style={styles.systemPillText} numberOfLines={1}>
                  {systemLabel}
                </Text>
              </View>
            </>
          ) : null}
        </View>
      </Pressable>

      {/* ACTIONS — reschedule + skip. Compact icon-only on mobile,
          icon-only on desktop too (label is in accessibility). Tooltips
          on web come from accessibilityLabel naturally. */}
      {!onRedo ? (
        <View style={styles.actions}>
          {onReschedule ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Reschedule ${task.title}`}
              onPress={handleReschedule}
              hitSlop={6}
              style={({ pressed, hovered }) => [
                styles.actionBtn,
                webTransition,
                hovered && styles.actionBtnHover,
                pressed && { transform: [{ scale: 0.9 }] },
              ]}>
              <CalendarClock size={14} color={Palette.inkSoft} strokeWidth={2} />
            </Pressable>
          ) : null}
          {onSkip ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Skip ${task.title}`}
              onPress={handleSkip}
              hitSlop={6}
              style={({ pressed, hovered }) => [
                styles.actionBtn,
                webTransition,
                hovered && styles.actionBtnHoverSkip,
                pressed && { transform: [{ scale: 0.9 }] },
              ]}>
              <SkipForward size={14} color={Palette.clay} strokeWidth={2} />
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  // Stage for the swipe gesture. Holds the foreground row plus the
  // reveal backgrounds. `overflow: hidden` clips the backgrounds when
  // the row is at rest so they don't peek past the rounded corners.
  swipeWrap: {
    position: 'relative',
    borderRadius: Radius.lg,
    overflow: 'hidden',
  },
  // Reveal backgrounds — full-bleed within the wrap, hidden by default
  // via opacity 0 driven by the animated style. Right-side = green
  // "Done", left-side = clay "Skip".
  swipeBg: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    gap: 8,
  },
  swipeBgComplete: {
    left: 0,
    right: 0,
    backgroundColor: Palette.moss,
    justifyContent: 'flex-start',
  },
  swipeBgSkip: {
    left: 0,
    right: 0,
    backgroundColor: Palette.clay,
    justifyContent: 'flex-end',
  },
  swipeBgText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.cream,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingHorizontal: 10,
    paddingVertical: 8,
    minHeight: 48,
  },
  rowHover: {
    backgroundColor: '#F4F7F2',
    borderColor: Palette.forestLight,
  },
  rowOverdue: {
    backgroundColor: 'rgba(176,95,60,0.06)',
    borderColor: 'rgba(176,95,60,0.3)',
  },
  rowNextYear: { opacity: 0.6 },
  // Round checkbox — 22px (Todoist-ish). Inset border so it reads as a
  // toggle, not a button.
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.8,
    borderColor: Palette.forestGlow,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOverdue: { borderColor: Palette.clay },
  checkboxHover: {
    borderColor: Palette.forest,
    backgroundColor: '#EAF0EA',
  },
  checkboxInner: {
    width: 14,
    height: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Body is the click-to-open zone. Negative margin extends it to the
  // edges of the row for a generous hit area without changing layout.
  body: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 2,
  },
  title: {
    fontSize: 13.5,
    fontFamily: Fonts.sansSemibold,
    color: Palette.ink,
    letterSpacing: -0.05,
    lineHeight: 17,
  },
  titleOverdue: { color: Palette.ink },
  metaLine: {
    marginTop: 3,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexWrap: 'nowrap',
  },
  dayPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: Palette.honeySoft,
  },
  dayPillOverdue: { backgroundColor: 'rgba(176,95,60,0.18)' },
  dayPillText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.honeyDeep,
    letterSpacing: 0.3,
  },
  dayPillTextOverdue: { color: Palette.clay },
  systemPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 999,
    backgroundColor: '#EAF0EA',
    maxWidth: 200,
  },
  systemPillText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 11.5,
    color: Palette.forest,
    flexShrink: 1,
  },
  meta: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkSoft,
  },
  metaMuted: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkFaint,
  },
  nextYearMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  metaOverdue: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.clay,
    letterSpacing: 0.1,
  },
  easy: { color: Palette.moss },
  moderate: { color: Palette.clay },
  dot: {
    width: 2.5,
    height: 2.5,
    borderRadius: 1.5,
    backgroundColor: Palette.inkFaint,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  actionBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnHover: { backgroundColor: '#EAF0EA' },
  actionBtnHoverSkip: { backgroundColor: 'rgba(176,95,60,0.13)' },
  redoPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1.2,
    borderColor: Palette.forest,
    backgroundColor: '#F4F7F2',
  },
  redoText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
});
